import React from 'react'
import { Box } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import ConfirmChangeDialog from './ConfirmChangeDialog'
import HistoryEntry from './HistoryEntry'
import { restoreTerm } from './languageTermsApi'
import { useTermRequestFlow } from './useTermRequestFlow'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

/**
 * Historia 176 — B5, CU7. The history of ONE key (the row's ⏱ dialog, RN6k).
 *
 * Props:
 * - `code` (string): custom language code.
 * - `termKey` (string): the key whose entries are shown.
 * - `entries` (TermChangeDto[]): ALL the entries of the language, most recent first (2.4); the panel
 *   filters by `termKey` and looks `restoredFromId` up in the whole list.
 * - `currentValue` (string): the key's value in `terms`; `''` when absent (inherited).
 * - `inherited` (string): the inherited term of the key: what is painted when `currentValue` is `''`, and
 *   what a restore to `null` ("inherited") paints (RN9b, T59).
 * - `readOnly` (boolean): file not readable or a 409 happened: no restore button (RN25).
 * - `artifact` (object): language catalogue artifact, for the RN9 confirmation.
 * - `systemValues` (object): story 204, `indexSystemValues` of C5: a system value asks for the global confirmation.
 * - `onFileUnavailable` (() => void): a 409 in restore puts the tab read-only.
 * - `onRestored` ((termKey, value, change) => void): restore answered 200; `change` is null when nothing was written.
 */
const TermHistoryPanel = ({
  code = '',
  termKey = '',
  entries = [],
  currentValue = '',
  inherited = '',
  readOnly = false,
  artifact = {},
  systemValues = NO_SYSTEM_VALUES,
  onFileUnavailable = () => {},
  onRestored = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  // B1: the very same request flow a row uses (RN9 confirmation, rejection map, 409): one function.
  const flow = useTermRequestFlow({ code, artifact, systemValues, onFileUnavailable, onApplied: (appliedKey, result) => onRestored(appliedKey, result.value, result.change) })
  const keyEntries = entries.filter((entry) => entry.key === termKey)

  // CU7: a key without any entry says so (T27).
  if (keyEntries.length === 0) return <span>{t_('No changes recorded')}</span>

  return (
    <>
      {flow.errorKey !== '' ? <Box role='alert'>{flow.errorMessage}</Box> : null}
      <Box component='ul'>
        {keyEntries.map((entry, index) => (
          <HistoryEntry
            key={entry.id}
            isLast={index === keyEntries.length - 1}
            entry={entry}
            entries={entries}
            currentValue={currentValue}
            readOnly={readOnly}
            onRestore={(state, changeId) => flow.submit(state.key, () => restoreTerm(code, changeId), {
              from: currentValue !== '' ? currentValue : inherited,
              to: state.newValue === null ? inherited : state.newValue
            })}
          />
        ))}
      </Box>
      <ConfirmChangeDialog
        open={flow.confirmOpen}
        termKey={flow.pendingTermKey}
        from={flow.pendingChange.from}
        to={flow.pendingChange.to}
        artifact={artifact}
        systemValues={systemValues}
        onConfirm={flow.handleConfirm}
        onCancel={flow.handleCancel}
      />
    </>
  )
}

export default TermHistoryPanel
